import React, { useState } from "react";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";

const UserCard = styled(Paper)(({ theme, active }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  backgroundColor: active ? "#f0fff0" : "#fff0f0", // Light green for active, light red for inactive
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

function AlignListItemCalls({ referralData }) {
  const [open, setOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Process referral data into levels with counts and user details
  const processReferralData = (data) => {
    const levels = {
      1: { length: 0, users: [] },
      2: { length: 0, users: [] },
      3: { length: 0, users: [] },
      4: { length: 0, users: [] },
      5: { length: 0, users: [] },
      6: { length: 0, users: [] },
      7: { length: 0, users: [] },
    };

    function formatDate(isoDate) {
      const date = new Date(isoDate);
      const day = date.getUTCDate().toString().padStart(2, "0");
      const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = date.getUTCFullYear();
      return `${day}/${month}/${year}`;
    }

    function processReferrals(referrals, currentLevel) {
      if (!referrals || !Array.isArray(referrals)) return;

      referrals.forEach((referral) => {
        if (levels[currentLevel]) {
          levels[currentLevel].length += 1;
          levels[currentLevel].users.push({
            name: referral.user_details?.first_name || "Unknown",
            email: referral.user_details?.email || "N/A", // Added email field
            dateOfJoining: referral.user_details?.createdAt
              ? formatDate(referral.user_details.createdAt)
              : "N/A",
            active:
              referral.is_subscribed?.is_subscribed === true
                ? true
                : referral.is_subscribed === null
                ? false
                : false,
            mobile: referral.user_details?.mobile_one || "N/A",
          });
        }
        processReferrals(referral.referrals, currentLevel + 1);
      });
    }

    processReferrals(data.referrals, 1);
    return levels;
  };

  const processedData = referralData ? processReferralData(referralData) : {};

  // Define faded background colors for each level
  const levelColors = {
    1: "rgba(144, 238, 144, 0.2)", // Light green
    2: "rgba(173, 216, 230, 0.2)", // Light blue
    3: "rgba(240, 230, 140, 0.2)", // Light yellow
    4: "rgba(221, 160, 221, 0.2)", // Light purple
    5: "rgba(255, 182, 193, 0.2)", // Light pink
    6: "rgba(176, 224, 230, 0.2)", // Powder blue
    7: "rgba(245, 245, 220, 0.2)", // Beige
  };

  // Define numeric image URLs for levels 1 through 7
  const levelImages = {
    1: "https://cdn.schoolstickers.com/products/en/819/16336-01.png",
    2: "https://cdn.schoolstickers.com/products/en/819/16336-02.png",
    3: "https://cdn.schoolstickers.com/products/en/819/16336-03.png",
    4: "https://cdn.schoolstickers.com/products/en/819/16336-04.png",
    5: "https://cdn.schoolstickers.com/products/en/819/16336-05.png",
    6: "https://cdn.schoolstickers.com/products/en/819/16336-06.png",
    7: "https://cdn.schoolstickers.com/products/en/819/16336-07.png",
  };

  // Handle opening the modal
  const handleOpen = (level) => {
    setSelectedLevel(level);
    setCurrentPage(1);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // Pagination logic
  const getPaginatedUsers = () => {
    if (!selectedLevel || !processedData[selectedLevel]) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return processedData[selectedLevel].users.slice(startIndex, endIndex);
  };

  const pageCount = selectedLevel
    ? Math.ceil(processedData[selectedLevel].users.length / itemsPerPage)
    : 0;

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <Box>
      {Object.entries(processedData).map(([level, data]) => (
        <List
          key={level}
          sx={{
            width: "100%",
            bgcolor: levelColors[level],
            px: { xs: 1, sm: 2, md: 3 },
            mb: 2,
            borderRadius: 1,
            cursor: "pointer",
            transition: "transform 0.2s",
            "&:hover": {
              transform: "scale(1.02)",
            },
          }}
          onClick={() => handleOpen(level)}
        >
          <ListItem alignItems="flex-start">
            <ListItemAvatar
              sx={{
                width: { xs: "20%", sm: 72, md: 80 },
                minWidth: { xs: "20%", sm: 72, md: 80 },
              }}
            >
              <Avatar
                alt={`Level ${level}`}
                src={levelImages[level] || "/static/images/avatar/demo.jpg"}
                sx={{
                  width: { xs: 48, sm: 60, md: 64 },
                  height: { xs: 48, sm: 60, md: 64 },
                }}
              />
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                  }}
                >
                  {`Level ${level}: ${data.length} ${data.length === 1 ? "person" : "persons"}`}
                </Typography>
              }
            />
          </ListItem>
          <Divider variant="inset" component="li" />
        </List>
      ))}

      {/* Modal for user details */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="user-details-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            p: { xs: 2, sm: 3, md: 4 },
            width: { xs: "95%", sm: "80%", md: "60%" },
            maxHeight: "85vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            id="user-details-modal"
            variant="h5"
            component="h2"
            sx={{
              mb: 3,
              textAlign: "center",
              fontWeight: "bold",
              color: "#1976d2",
            }}
          >
            {`Level ${selectedLevel} Users`}
          </Typography>

          {/* User list with card UI */}
          <Box>
            {getPaginatedUsers().map((user, index) => (
              <UserCard key={index} active={user.active}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "medium",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      mr: 2,
                    }}
                  >
                    {`${index + 1 + (currentPage - 1) * itemsPerPage}. ${user.name}`}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    }}
                  >
                    {`Email: ${user.email}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    }}
                  >
                    {`Joined: ${user.dateOfJoining}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    }}
                  >
                    {`Mobile: ${user.mobile}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: user.active ? "green" : "red",
                      fontWeight: "medium",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    }}
                  >
                    {user.active ? "Active" : "Inactive"}
                  </Typography>
                </Box>
              </UserCard>
            ))}
          </Box>

          {/* Pagination */}
          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
}

AlignListItemCalls.propTypes = {
  referralData: PropTypes.object,
};

export default AlignListItemCalls;
